import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getAgentActivityLogs } from "../../redux/thunk";
import { clearActivityLogs, clearError } from "../../redux/agents/agentsSlice";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import Pagination from "../../components/Pagination";
import axios from "axios";
import { IoArrowBack, IoRefresh } from "react-icons/io5";

const ActivityLog = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { activityLogs, status, error } = useAppSelector(
    (store) => store.agents
  );
  const { token } = useAppSelector((store) => store.auth);

  // Debug logging
  console.log("ActivityLog component rendered", {
    activityLogs,
    status,
    error,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [filterEventType, setFilterEventType] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [agents, setAgents] = useState<any[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const itemsPerPage = 10;

  // Fetch agents list for the dropdown
  const fetchAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/agents`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
    } finally {
      setLoadingAgents(false);
    }
  }, [token]);

  // Fetch activity logs with current filters
  const fetchActivityLogs = useCallback(() => {
    const params: any = {};
    if (selectedAgentId) params.agentId = selectedAgentId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    dispatch(getAgentActivityLogs(params));
  }, [dispatch, selectedAgentId, startDate, endDate]);

  useEffect(() => {
    fetchAgents();
    fetchActivityLogs();

    return () => {
      dispatch(clearActivityLogs());
      dispatch(clearError());
    };
  }, [dispatch, fetchAgents, fetchActivityLogs]);

  // Refetch when filters change
  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  // Filter logs based on search and filter criteria
  const filteredLogs = activityLogs.filter((log) => {
    const matchesEventType =
      !filterEventType || log.eventType === filterEventType;
    const matchesAction = !filterAction || log.action === filterAction;
    const matchesEmail =
      !searchEmail ||
      log.description.toLowerCase().includes(searchEmail.toLowerCase());

    return matchesEventType && matchesAction && matchesEmail;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredLogs.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Get unique event types and actions for filter dropdowns
  const eventTypes = Array.from(
    new Set(activityLogs.map((log) => log.eventType))
  );
  const actions = Array.from(new Set(activityLogs.map((log) => log.action)));

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType) {
      case "target_reached":
        return "bg-green-100 text-green-800";
      case "target_missed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/dashboard/agents")}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition"
              >
                <IoArrowBack className="w-5 h-5" />
                <span>Back to Agents</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Agent Activity Logs
                </h1>
                <p className="text-sm text-gray-600">
                  Monitor agent performance and activities
                </p>
              </div>
            </div>
            <button
              onClick={() => fetchActivityLogs()}
              className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary/90 transition"
              disabled={status === "loading"}
            >
              <IoRefresh className="w-4 h-4" />
              <span>{status === "loading" ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="grid items-center grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Agent
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loadingAgents}
              >
                <option value="">All Agents</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {agent.firstname} {agent.lastname}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search by Email
              </label>
              <input
                type="text"
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                placeholder="Search by email..."
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
              />
            </div> */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={filterEventType}
                onChange={(e) => setFilterEventType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
              >
                <option value="">All Event Types</option>
                {eventTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSelectedAgentId("");
                  setStartDate("");
                  setEndDate("");
                  setFilterEventType("");
                  setFilterAction("");
                  setSearchEmail("");
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600 transition"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Second row for Action filter */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action
              </label>
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary"
              >
                <option value="">All Actions</option>
                {actions.map((action) => (
                  <option key={action} value={action}>
                    {action.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => fetchActivityLogs()}
                className="bg-secondary text-white px-4 py-2 rounded-md hover:bg-secondary-dark transition"
                disabled={status === "loading"}
              >
                {status === "loading" ? "Refreshing..." : "Refresh Data"}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            {status === "loading" ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-center">
                <p className="font-medium">Error loading activity logs</p>
                <p className="text-sm mt-1">{error}</p>
                <button
                  onClick={() => fetchActivityLogs()}
                  className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
                >
                  Retry
                </button>
              </div>
            ) : currentItems.length > 0 ? (
              <div className="space-y-4">
                {currentItems.map((log) => (
                  <div
                    key={log._id}
                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getEventTypeColor(
                            log.eventType
                          )}`}
                        >
                          {log.eventType.replace("_", " ").toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500">
                          {log.action.replace("_", " ").toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDate(log.createdAt)}
                      </span>
                    </div>

                    <p className="text-gray-800 mb-3">{log.description}</p>

                    {log.metadata && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Details
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {log.metadata.targetValue && (
                            <div>
                              <span className="text-gray-500">Target:</span>
                              <span className="ml-1 font-medium">
                                {log.metadata.targetValue}
                              </span>
                            </div>
                          )}
                          {log.metadata.actualOrders !== undefined && (
                            <div>
                              <span className="text-gray-500">
                                Actual Orders:
                              </span>
                              <span className="ml-1 font-medium">
                                {log.metadata.actualOrders}
                              </span>
                            </div>
                          )}
                          {log.metadata.totalSales !== undefined && (
                            <div>
                              <span className="text-gray-500">
                                Total Sales:
                              </span>
                              <span className="ml-1 font-medium">
                                {formatCurrency(log.metadata.totalSales)}
                              </span>
                            </div>
                          )}
                          {log.metadata.earnings !== undefined && (
                            <div>
                              <span className="text-gray-500">Earnings:</span>
                              <span className="ml-1 font-medium">
                                {formatCurrency(log.metadata.earnings)}
                              </span>
                            </div>
                          )}
                          {log.metadata.period && (
                            <div>
                              <span className="text-gray-500">Period:</span>
                              <span className="ml-1 font-medium">
                                {log.metadata.period}
                              </span>
                            </div>
                          )}
                          {log.metadata.shortfall !== undefined && (
                            <div>
                              <span className="text-gray-500">Shortfall:</span>
                              <span className="ml-1 font-medium text-red-600">
                                {log.metadata.shortfall}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg mb-2">
                  No activity logs found
                </div>
                <p className="text-gray-500">
                  {filteredLogs.length === 0 && activityLogs.length > 0
                    ? "Try adjusting your filters"
                    : "No agent activity logs available"}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredLogs.length > itemsPerPage && (
            <div className="border-t bg-gray-50 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(startIndex + itemsPerPage, filteredLogs.length)} of{" "}
                  {filteredLogs.length} logs
                </div>
                <Pagination
                  currentPage={currentPage}
                  length={filteredLogs.length}
                  itemsPerPage={itemsPerPage}
                  handlePagination={setCurrentPage}
                  prevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  nextPage={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
